import { useEffect, useState, useCallback } from "react";
import { TextField, Grid, Button, Typography, Alert, FormLabel, Chip, Box } from "@mui/material";
import styles from './index.module.css';
import { deleteComment, editComment, getProductComments, postComment } from "../../../services/presentation";
import Snackbar from '@mui/material/Snackbar';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ButtonLoader from "../../ButtonLoader";

const Comment = ({ productData, presentationId }) => {
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [commentsList, setCommentsList] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: 'error', message: '' });
  const resolvedProductId =
    productData?.Id ??
    productData?.id ??
    productData?.ProductId ??
    productData?.productId;

  // Fetch Comments
  const fetchComments = useCallback(async () => {
    if (!presentationId || !resolvedProductId) {
      setCommentsList([]);
      return;
    }
    try {
      const response = await getProductComments(presentationId, resolvedProductId);
      setCommentsList(Array.isArray(response?.comments) ? response.comments : []);
    } catch (error) {
      setToast({ open: true, severity: 'error', message: 'Failed to fetch comments' });
    }
  }, [presentationId, resolvedProductId]);

  // Handle Toast Close
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ open: false, severity: 'success' });
  };

  // Add/Update Comment
  const handleCommentAction = async () => {
    if (comment.trim() === "") {
      setToast({ open: true, severity: 'warning', message: 'Please enter a comment' });
      return;
    }

    setLoading(true);
    if (!resolvedProductId || !presentationId) {
      setToast({ open: true, severity: 'warning', message: 'Missing presentation/product reference' });
      return;
    }

    const payload = { comment, productId: resolvedProductId, presentationId };

    try {
      if (editingCommentId) {
        await editComment(editingCommentId, comment);
        setToast({ open: true, severity: 'success', message: 'Comment updated successfully' });
      } else {
        await postComment(payload);
        setToast({ open: true, severity: 'success', message: 'Comment added successfully' });
      }
      setComment('');
      setEditingCommentId(null);
      fetchComments(); // Refresh the comments list
    } catch (error) {
      setToast({ open: true, severity: 'error', message: 'Failed to submit comment' });
    } finally {
      setLoading(false);
    }
  };

  // Start editing a comment
  const handleStartEdit = (commentId, currentComment) => {
    setEditingCommentId(commentId);
    setComment(currentComment);
  };

  // Delete Comment
  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      setToast({ open: true, severity: 'success', message: 'Comment deleted successfully' });
      fetchComments(); // Refresh the comments list
    } catch (error) {
      setToast({ open: true, severity: 'error', message: 'Failed to delete comment' });
    }
  };

  // Effect for fetching comments initially
  useEffect(() => {
    if (presentationId && resolvedProductId) {
      fetchComments();
    }
  }, [presentationId, resolvedProductId, fetchComments]);

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={toast.open}
        onClose={handleCloseToast}
        autoHideDuration={3000}
      >
        <Alert severity={toast.severity} onClose={handleCloseToast}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Grid className="user-write-a-comment" item xs={11}>
        <FormLabel className={styles.MuiFormLabelRoot}>Write a comment</FormLabel>

        <Box className={styles.CommentInputWrapper}>
          <Box className={styles.ChipWrapper}>
            {commentsList?.map((commentItem) => (
              <Chip
                key={commentItem.commentId}
                label={
                  <Box className={styles.ChipContent}>
                    <Typography className={styles.ChipTypography}>
                      {commentItem.comment}
                    </Typography>
                    <Box className={styles.ChipActions}>
                      <EditIcon
                        className={styles.EditIconStyle} onClick={() => handleStartEdit(commentItem.commentId, commentItem.comment)} />
                      <DeleteIcon
                        className={styles.DeleteIconStyle}
                        onClick={() => handleDeleteComment(commentItem.commentId)} />
                    </Box>
                  </Box>
                }
                color="primary"
                variant="outlined"
                sx={{
                  backgroundColor: '#fff', color: '#333', border: '1px solid #ddd', height: '28px',
                  '& .MuiChip-deleteIcon': { fontSize: '16px' },
                }}
              />
            ))}
          </Box>

          <TextField
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment"
            fullWidth
            multiline
            rows={2}
            InputProps={{ disableUnderline: true, sx: { padding: 0 } }}
            className={styles.CommentInput}
          />
        </Box>

        <Button
          variant="contained"
          className={`${styles.AddCommentButton}`}
          onClick={handleCommentAction}
          disabled={loading}
        >
          <Typography className={styles.ButtonTypography}>
            {loading ? <ButtonLoader /> : editingCommentId ? 'Save' : 'Add comment'}
          </Typography>
        </Button>
      </Grid>
    </>
  );
};

export default Comment;
